import React, { useState } from "react";
import { MarketingLayout } from "@/marketing/MarketingLayout";
import { Seo } from "@/components/Seo";
import { SectionLabel, IDButton, Monogram, TAGLINE } from "@/marketing/brand";
import { SignupForm } from "@/marketing/SignupForm";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Check, Sparkles, BookOpen, Compass, Sprout, Gift } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

/*
 * /sunday-reset — Sunday Reset newsletter landing page (PUBLIC).
 * Audience: Christian women (25–45, professional/entrepreneurial).
 * Voice: warm, wise, Scripture-rooted, grace over shame, ambitious without hustle —
 * "calm guidance from a discerning older sister." Copy aligned to the Intrinsic
 * Daily business plan (mission, values, Five Life Pillars, signature language).
 *
 * Email capture posts to /api/newsletter/subscribe (MongoDB + MailerLite when set).
 * The lead-magnet asset/delivery is intentionally not built yet — the "welcome
 * gift" module is styled and ready for its content to drop in.
 */

/* ---------------- Presentational helper ---------------- */
const Section = ({ id, className = "", children }) => (
  <section id={id} data-testid={`sunday-reset-${id}`} className={className}>
    <div className="mx-auto max-w-[1280px] px-5 sm:px-8">{children}</div>
  </section>
);

const RECEIVES = [
  { icon: BookOpen, title: "A word to center on", body: "One passage, read in context — never proof-texted — to steady your heart before the week begins." },
  { icon: Compass, title: "One gentle reset", body: "A simple practice to close last week with honesty and grace, then step into the next with clarity." },
  { icon: Sparkles, title: "A question worth sitting with", body: "One honest prompt to help you steward your time, energy, and attention toward what truly matters." },
  { icon: Sprout, title: "One pillar to tend", body: "A small, doable focus across your five life pillars — faith, health, work, wealth, relationships — so your whole life grows together." },
];

const FOR_YOU = [
  "You're a Christian woman carrying real responsibility — work, home, calling — and you want your faith woven through all of it.",
  "You're tired of holding life together with scattered planners, apps, and devotionals that don't speak to each other.",
  "You'd rather reset with grace on Sunday than react your way through the week.",
  "You believe you can be ambitious and faithful — that excellence and rest can share the same week.",
];

const NOT_FOR_YOU = [
  "You're looking for hustle hacks and 5 a.m. shame.",
  "You'd rather keep faith in a separate box from the rest of your life.",
  "You want a higher productivity score more than a calmer, more faithful week.",
];

const FAQS = [
  ["What is the Sunday Reset?", "A short weekly letter for Christian women that lands on Sunday to help you close the week with grace and begin the next on purpose — one Scripture to center on, one gentle reset, and one life pillar to tend."],
  ["How often will I hear from you?", "Once a week, on Sunday. One substantive letter — no daily clutter, no inbox noise."],
  ["Is it explicitly Christian?", "Yes — it's faith-centered and Scripture-rooted, read in context and never proof-texted. It's written for Christian women, and just as warmly for anyone who is curious, seeking, or simply open to the Lord. Always invitational, never preachy, and never shaming — you're welcome exactly as you are, in whatever season you're in."],
  ["Is it really free?", "Completely free — and you'll receive a welcome gift, The Sunday Reset Starter, when you subscribe."],
  ["Do I need the Intrinsic Daily app?", "Not at all. The Sunday Reset stands on its own. It's simply the front porch of the larger Intrinsic Daily vision — one gentle operating system for a whole, faithful life — which you're welcome to explore later, on your terms."],
  ["What will you do with my email?", "We use it to send you the Sunday Reset, your welcome gift, and occasional updates from Intrinsic Daily — including news about the Life OS we're building. That's all: no selling, and we never share your details with anyone. Unsubscribe with one click anytime."],
];

export default function SundayReset() {
  return (
    <MarketingLayout>
      <Seo slug="sunday-reset" />
      <div data-testid="sunday-reset-page" aria-label="Sunday Reset newsletter">

        {/* ---------------- Hero ---------------- */}
        <Section id="hero" className="pt-14 pb-16 sm:pt-20 lg:pt-24 lg:pb-24">
          <div className="grid items-center gap-12 lg:grid-cols-[1.1fr_0.9fr] lg:gap-16">
            {/* Left — message + signup */}
            <div className="text-center lg:text-left">
              <SectionLabel className="!tracking-[0.3em]">The Sunday Reset · a weekly letter for Christian women</SectionLabel>
              <h1 className="id-serif mt-6 text-[2.5rem] leading-[1.04] text-[var(--id-charcoal)] sm:text-6xl lg:text-[4.5rem]">
                Close the week with grace. Begin the next on purpose.
              </h1>
              <p className="id-sans mx-auto mt-6 max-w-[540px] text-[18px] leading-relaxed text-[var(--id-charcoal)]/70 lg:mx-0">
                A quiet Sunday letter for the Christian woman carrying a full, capable life — one Scripture
                to center on, one gentle reset, and one honest question to help your faith and your week
                finally speak to each other.
              </p>
              <div className="mx-auto mt-9 max-w-[560px] text-left lg:mx-0">
                <SignupForm testidPrefix="hero-signup" />
              </div>
            </div>
            {/* Right — editorial letter card */}
            <div className="relative">
              <div className="rounded-[28px] border border-[color:rgba(32,32,32,0.10)] bg-white px-8 py-10 shadow-[0_44px_90px_-45px_rgba(43,38,34,0.45)] sm:px-11 sm:py-12">
                <div className="flex items-center justify-between">
                  <span className="id-sans text-[11px] font-semibold uppercase tracking-[0.24em] text-[var(--id-gold)]">This Sunday</span>
                  <Monogram className="h-9" />
                </div>
                <p className="id-serif mt-9 text-[2rem] leading-[1.24] text-[var(--id-charcoal)] sm:text-[2.15rem]">
                  "What would it look like to live this week as the woman God has already called you to be?"
                </p>
                <div className="mt-9 h-px w-full bg-[color:rgba(32,32,32,0.10)]" />
                <p className="id-sans mt-6 text-[16px] leading-relaxed text-[var(--id-charcoal)]/60">
                  One word to center on. One gentle reset. One pillar to tend — in ten unhurried minutes, every Sunday.
                </p>
                <p className="id-serif mt-6 text-xl text-[var(--id-walnut)]">— Intrinsic Daily</p>
              </div>
            </div>
          </div>
        </Section>

        {/* ---------------- Reader recognition ---------------- */}
        <Section id="reader-recognition" className="py-16 sm:py-20">
          <div className="rounded-3xl border border-[color:rgba(32,32,32,0.10)] bg-[var(--id-ivory)] px-6 py-14 sm:px-14 sm:py-16">
            <div className="mx-auto max-w-[740px] text-center">
              <SectionLabel>You're not behind. You're just carrying a lot.</SectionLabel>
              <p className="id-serif mt-6 text-2xl leading-relaxed text-[var(--id-charcoal)] sm:text-[1.75rem]">
                You don't need another planner, another app, or another 5 a.m. rule you'll abandon by
                Thursday. You have a devotional life and a planning system — they just don't speak to each
                other. What you need is a calm rhythm you can return to, week after week.
              </p>
              <p className="id-sans mt-6 text-[15px] leading-relaxed text-[var(--id-charcoal)]/65">
                The Sunday Reset is that rhythm. Ten unhurried minutes to let your faith shape your week —
                no pressure, no catching up, no shame.
              </p>
            </div>
          </div>
        </Section>

        {/* ---------------- What she receives ---------------- */}
        <Section id="what-she-receives" className="py-16 sm:py-20">
          <div className="max-w-[640px]">
            <SectionLabel>Inside every letter</SectionLabel>
            <h2 className="id-serif mt-4 text-[2.1rem] text-[var(--id-charcoal)] sm:text-[2.75rem]">What arrives each Sunday</h2>
            <p className="id-sans mt-4 text-[15px] leading-relaxed text-[var(--id-charcoal)]/65">
              Short by design and rooted in Scripture. Nothing to manage — everything to help you begin again.
            </p>
          </div>
          <div className="mt-10 grid gap-5 sm:grid-cols-2">
            {RECEIVES.map((r) => {
              const Icon = r.icon;
              return (
                <div key={r.title} className="rounded-2xl border border-[color:rgba(32,32,32,0.10)] bg-white px-6 py-6 transition-colors hover:border-[var(--id-gold)]/50 sm:px-7 sm:py-7">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[var(--id-gold)]/12 text-[var(--id-gold)]">
                    <Icon size={20} strokeWidth={1.8} />
                  </span>
                  <h3 className="id-serif mt-4 text-2xl text-[var(--id-charcoal)]">{r.title}</h3>
                  <p className="id-sans mt-2 text-[15px] leading-relaxed text-[var(--id-charcoal)]/65">{r.body}</p>
                </div>
              );
            })}
          </div>
        </Section>

        {/* ---------------- Lead magnet (welcome gift) ---------------- */}
        <Section id="lead-magnet" className="py-16 sm:py-20">
          <div className="overflow-hidden rounded-3xl bg-[var(--id-walnut)] text-[var(--id-ivory)]">
            <div className="grid items-center gap-8 px-7 py-12 sm:px-14 sm:py-16 lg:grid-cols-[1.1fr_1fr] lg:gap-12">
              <div>
                <span className="id-sans inline-flex items-center gap-2 rounded-full bg-white/10 px-3.5 py-1.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-[var(--id-gold)]">
                  <Gift size={13} /> Free welcome gift
                </span>
                <h2 className="id-serif mt-5 text-3xl leading-tight sm:text-4xl">The Sunday Reset Starter</h2>
                <p className="id-sans mt-4 max-w-[440px] text-[15px] leading-relaxed text-[var(--id-ivory)]/75">
                  A simple, beautiful guide to run your own weekly reset — the gentle rhythm behind every
                  letter, built around the five callings you already carry. Yours the moment you subscribe.
                </p>
                <p className="id-sans mt-3 text-[12px] uppercase tracking-[0.18em] text-[var(--id-ivory)]/45">
                  Included with your first Sunday Reset
                </p>
              </div>
              <div className="rounded-2xl bg-[var(--id-charcoal)]/30 p-6 backdrop-blur-sm">
                <SignupForm dark testidPrefix="magnet-signup" cta="Claim my gift" />
              </div>
            </div>
          </div>
        </Section>

        {/* ---------------- Newsletter preview (the spirit of it) ---------------- */}
        <Section id="newsletter-preview" className="py-16 sm:py-20">
          <div className="mx-auto max-w-[660px] text-center">
            <SectionLabel>The heart of it</SectionLabel>
            <h2 className="id-serif mt-4 text-[2.1rem] text-[var(--id-charcoal)] sm:text-[2.75rem]">What every letter will hold</h2>
            <p className="id-sans mx-auto mt-4 max-w-[520px] text-[15px] leading-relaxed text-[var(--id-charcoal)]/65">
              We're still shaping the very first issue. However each Sunday Reset takes shape, this is the
              spirit it will carry — short, unhurried, and rooted in Scripture.
            </p>
          </div>
          <div className="mx-auto mt-10 max-w-[680px]">
            <div className="rounded-2xl border border-[color:rgba(32,32,32,0.10)] bg-white px-7 py-9 shadow-[0_20px_50px_-30px_rgba(43,38,34,0.35)] sm:px-10 sm:py-10">
              <p className="id-serif text-2xl leading-snug text-[var(--id-charcoal)]">
                "What would it look like to live this week as the woman God has already called you to be?"
              </p>
              <div className="mt-8 space-y-6 border-t border-[color:rgba(32,32,32,0.10)] pt-7">
                <div>
                  <p className="id-sans text-[11px] font-semibold uppercase tracking-[0.2em] text-[var(--id-gold)]">Center on</p>
                  <p className="id-sans mt-1.5 text-[14px] leading-relaxed text-[var(--id-charcoal)]/70">A short passage — read in context — to ground your week in something steadier than your to-do list.</p>
                </div>
                <div>
                  <p className="id-sans text-[11px] font-semibold uppercase tracking-[0.2em] text-[var(--id-gold)]">Gentle reset</p>
                  <p className="id-sans mt-1.5 text-[14px] leading-relaxed text-[var(--id-charcoal)]/70">Name one grace from last week, release one thing, and choose one intention for the days ahead.</p>
                </div>
                <div>
                  <p className="id-sans text-[11px] font-semibold uppercase tracking-[0.2em] text-[var(--id-gold)]">One pillar to tend</p>
                  <p className="id-sans mt-1.5 text-[14px] leading-relaxed text-[var(--id-charcoal)]/70">A single, doable focus for the week — faith, health, work, wealth, or relationships.</p>
                </div>
              </div>
            </div>
            <p className="id-sans mt-3 text-center text-[12px] text-[var(--id-charcoal)]/40">
              A glimpse of the intention behind each letter — the design is still being lovingly made.
            </p>
          </div>
        </Section>

        {/* ---------------- Who it is for ---------------- */}
        <Section id="who-it-is-for" className="py-16 sm:py-20">
          <div className="max-w-[640px]">
            <SectionLabel>An honest fit</SectionLabel>
            <h2 className="id-serif mt-4 text-[2.1rem] text-[var(--id-charcoal)] sm:text-[2.75rem]">Who the Sunday Reset is for</h2>
          </div>
          <div className="mt-10 grid gap-6 lg:grid-cols-2">
            <div className="rounded-2xl border border-[color:rgba(32,32,32,0.10)] bg-white px-7 py-8">
              <p className="id-sans text-[11px] font-semibold uppercase tracking-[0.22em] text-[var(--id-gold)]">Written for you if…</p>
              <ul className="mt-5 space-y-4">
                {FOR_YOU.map((t) => (
                  <li key={t} className="flex gap-3">
                    <Check size={18} className="mt-0.5 shrink-0 text-[var(--id-gold)]" />
                    <span className="id-sans text-[14.5px] leading-relaxed text-[var(--id-charcoal)]/75">{t}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl border border-[color:rgba(32,32,32,0.10)] bg-[var(--id-ivory)] px-7 py-8">
              <p className="id-sans text-[11px] font-semibold uppercase tracking-[0.22em] text-[var(--id-charcoal)]/45">Probably not for you if…</p>
              <ul className="mt-5 space-y-4">
                {NOT_FOR_YOU.map((t) => (
                  <li key={t} className="flex gap-3">
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--id-charcoal)]/25" />
                    <span className="id-sans text-[14.5px] leading-relaxed text-[var(--id-charcoal)]/55">{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Section>

        {/* ---------------- Intrinsic Daily note ---------------- */}
        <Section id="intrinsic-daily-note" className="py-16 sm:py-20">
          <div className="mx-auto max-w-[720px] rounded-3xl border border-[color:rgba(32,32,32,0.10)] bg-white px-7 py-12 text-center sm:px-14 sm:py-16">
            <Monogram className="mx-auto h-12" />
            <p className="id-sans mt-6 text-[11px] font-semibold uppercase tracking-[0.28em] text-[var(--id-gold)]">A note from Intrinsic Daily</p>
            <p className="id-serif mt-5 text-[1.55rem] leading-relaxed text-[var(--id-charcoal)]">
              The Sunday Reset is where it begins. Before the app, before the dashboards, there's one
              conviction: your worth is God-given, and daily life is where your faith, values, and calling
              become visible.
            </p>
            <p className="id-sans mt-5 text-[15px] leading-relaxed text-[var(--id-charcoal)]/60">
              We're building one gentle operating system for a whole, faithful life. Intrinsic Daily won't
              tell you who to become — it helps you live in alignment with the woman God has already called
              and equipped you to be. This letter is where we begin growing alongside you.
            </p>
            <p className="id-serif mt-6 text-xl text-[var(--id-walnut)]">{TAGLINE}</p>
          </div>
        </Section>

        {/* ---------------- Final signup ---------------- */}
        <Section id="final-signup" className="py-20 sm:py-24">
          <div className="rounded-3xl bg-[var(--id-charcoal)] px-6 py-14 text-center sm:px-14 sm:py-20">
            <SectionLabel>Begin this Sunday</SectionLabel>
            <h2 className="id-serif mx-auto mt-5 max-w-[640px] text-4xl leading-tight text-[var(--id-ivory)] sm:text-5xl">
              Give your best energy to what matters most.
            </h2>
            <p className="id-sans mx-auto mt-5 max-w-[520px] text-[15px] leading-relaxed text-[var(--id-ivory)]/65">
              Join the Christian women resetting with grace and intention every Sunday. One letter. A
              welcome gift. No noise, no shame.
            </p>
            <div className="mx-auto mt-9 max-w-[620px] text-left">
              <SignupForm dark testidPrefix="final-signup" cta="Subscribe free" />
            </div>
          </div>
        </Section>

        {/* ---------------- FAQ ---------------- */}
        <Section id="faq" className="pb-24 sm:pb-28">
          <div className="mx-auto max-w-[760px]">
            <div className="text-center">
              <SectionLabel>Good questions</SectionLabel>
              <h2 className="id-serif mt-4 text-[2.1rem] text-[var(--id-charcoal)] sm:text-[2.75rem]">Before you subscribe</h2>
            </div>
            <Accordion type="single" collapsible className="mt-10">
              {FAQS.map(([q, a], i) => (
                <AccordionItem key={q} value={`faq-${i}`} className="border-b border-[color:rgba(32,32,32,0.10)]">
                  <AccordionTrigger data-testid={`faq-trigger-${i}`} className="id-serif text-left text-[1.15rem] text-[var(--id-charcoal)] hover:no-underline">
                    {q}
                  </AccordionTrigger>
                  <AccordionContent className="id-sans text-[14.5px] leading-relaxed text-[var(--id-charcoal)]/65">
                    {a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </Section>

      </div>
    </MarketingLayout>
  );
}
